import { TextField } from "@material-ui/core";
import { CheckBox, CheckBoxOutlineBlank } from "@material-ui/icons";
import { StyledAutoComplete } from "../styles/MaterialGroopTheme";
import { IUserDTO, UserDTO, useGroupContext } from "groopit-js";
import React, { useEffect, useState } from "react";
import {
  FilterPickerVariants,
  getPopperComponent,
  isOpenByDefault,
} from "./FilterPicker";
import { useDebounce } from "react-use";

interface PeoplePickerProps {
  onChange: (selectedPeople: UserDTO[]) => void;
  selectedPeople: UserDTO[];
  variant: FilterPickerVariants;
}

class PeoplePickerOption {
  option: IUserDTO | UserDTO | null;
  isValid: boolean;

  constructor(option: IUserDTO | UserDTO | null, isValid: boolean) {
    this.option = option;
    this.isValid = isValid;
  }

  get label(): string {
    if (this.option) {
      if (this.isValid) {
        return this.option.friendlyName;
      } else {
        return `${this.option.friendlyName} (\u26a0 person deleted)`;
      }
    } else {
      return "All";
    }
  }

  get value(): UserDTO {
    if (!this.option) {
      throw new Error("Cannot get value of 'All' option");
    }

    return new UserDTO({ ...this.option });
  }
}

export const PeoplePicker = (props: PeoplePickerProps) => {
  const { variant, selectedPeople, onChange } = {
    ...props,
  };
  const { users: allPeople } = useGroupContext();
  const [optionsList, setOptionsList] = useState<PeoplePickerOption[]>([
    new PeoplePickerOption(null, true),
  ]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const options: PeoplePickerOption[] = [];
    if (allPeople) {
      options.push(
        ...allPeople
          .map((p) => new PeoplePickerOption(p, true))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    }
    if (selectedPeople) {
      const invalidSelections = selectedPeople.filter(
        (s) => !allPeople.find((a) => a.id === s.id),
      );
      options.push(
        ...invalidSelections
          .map((s) => new PeoplePickerOption(s, false))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    }

    setOptionsList([new PeoplePickerOption(null, true), ...options]);
  }, [allPeople, selectedPeople]);

  // When using the inline variant there is a transition
  // effect that displays the popover that contains the
  // autocomplete. If we default the autocomplete to
  // open right away, it's position is wrong. So we delay
  // the opening slightly to give the animation time to
  // settle.
  useDebounce(
    () => {
      if (isOpenByDefault(variant)) {
        setOpen(true);
      }
    },
    100,
    [variant],
  );

  return (
    <StyledAutoComplete
      PopperComponent={(popperProps) => getPopperComponent(popperProps)}
      fullWidth
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      openOnFocus
      disableCloseOnSelect
      getOptionLabel={(o) => o.label}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          label={
            selectedPeople.length != 0
              ? `People (${
                  selectedPeople.length -
                  optionsList.filter((o) => !o.isValid).length
                } selected${
                  optionsList.some((o) => !o.isValid)
                    ? `, ${optionsList.filter((o) => !o.isValid).length} error`
                    : ""
                })`
              : "People (All)"
          }
          variant="filled"
        />
      )}
      renderTags={() => undefined}
      renderOption={(o, state) =>
        o.option ? (
          <div className="space-x-2 truncate">
            {state.selected ? <CheckBox /> : <CheckBoxOutlineBlank />}
            <span className={!o.isValid ? "italic text-app-gray-06" : ""}>
              {o.label}
            </span>
          </div>
        ) : (
          <div className="italic">All</div>
        )
      }
      options={optionsList}
      value={
        selectedPeople
          .map((s) => optionsList.find((o) => o.option?.id === s.id))
          .filter((o) => !!o) as PeoplePickerOption[]
      } // filter out nulls
      onChange={(_, value) =>
        onChange(
          value.some((o) => o.option === null) ? [] : value.map((o) => o.value),
        )
      }
    />
  );
};
